import React, { useState, useMemo } from 'react';
import { Plus, Edit, Trash2, Shield } from 'lucide-react';
import { SearchBar } from '../shared/SearchBar';
import { SortableTableHeader } from '../shared/SortableTableHeader';
import { Pagination } from '../shared/Pagination';
import { FormModal } from '../shared/FormModal';
import { ConfirmModal } from '../../../framework/modals/ConfirmModal';
import { CustomerDropdown } from '../shared/CustomerDropdown';
import { FormInput } from '../shared/FormInput';
import { FormTextarea } from '../shared/FormTextarea';
import { FormCheckbox } from '../shared/FormCheckbox';
import { FormSelect } from '../shared/FormSelect';
import { useInsuranceManagement } from '../hooks/useInsuranceManagement';
import { useModeratorData } from '../hooks/useModeratorData';
import { useTableSort } from '../hooks/useTableSort';
import { useModeratorPermissions } from '../hooks/useModeratorPermissions';
import type { Customer } from '../types/moderator.types';

export const InsurancesSection: React.FC = () => {
  const { insurances, customers, refetchInsurances, refetchModeratorData } = useModeratorData();
  const insurance = useInsuranceManagement(refetchInsurances, refetchModeratorData);
  const { canAdd, canChange, canDelete } = useModeratorPermissions();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const canCreateInsurance = selectedCustomer
    ? canAdd(selectedCustomer.id)
    : customers.some((customer) => canAdd(customer.id));

  const customerOptions = useMemo(() =>
    customers.map(c => ({ value: c.id, label: c.name })),
    [customers]
  );

  const filteredInsurances = insurances.filter(ins => {
    const matchesSearch = ins.name.toLowerCase().includes(insurance.searchTerm.toLowerCase());
    const matchesCustomer = !selectedCustomer || ins.customerId === selectedCustomer.id;
    return matchesSearch && matchesCustomer;
  });

  const {
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage
  } = useTableSort(filteredInsurances, 'name', 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Insurance Management
        </h2>
        {canCreateInsurance && (
        <button
          onClick={() => insurance.handleCreate(selectedCustomer?.id)}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4" />
          <span>Add Insurance</span>
        </button>
        )}
      </div>

      <CustomerDropdown
        customers={customers}
        selectedCustomer={selectedCustomer}
        onSelectCustomer={(customer) => {
          setSelectedCustomer(customer);
          insurance.setSearchTerm('');
        }}
        helperText="Select your organization to access insurances"
      />

      <SearchBar
        value={insurance.searchTerm}
        onChange={insurance.setSearchTerm}
        placeholder={selectedCustomer ? `Search insurances for ${selectedCustomer.name}...` : 'Search all insurances...'}
      />
      {selectedCustomer && (
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Showing insurances for {selectedCustomer.name}
        </p>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredInsurances.length}
      />

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
              <SortableTableHeader
                field="name"
                label="Name"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="text-left p-3" style={{ color: 'var(--color-text-secondary)' }}>Description</th>
              <th className="text-left p-3" style={{ color: 'var(--color-text-secondary)' }}>Customer</th>
              <SortableTableHeader
                field="isActive"
                label="Status"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="text-right p-3" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((ins) => (
              <tr key={ins.id} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <td className="p-3" style={{ color: 'var(--color-text-primary)' }}>{ins.name}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{ins.description}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{ins.customer?.name ?? '—'}</td>
                <td className="p-3">
                  <span
                    className="px-2 py-1 rounded text-xs"
                    style={{
                      backgroundColor: ins.isActive ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
                      color: ins.isActive ? 'var(--color-success)' : 'var(--color-error)'
                    }}
                  >
                    {ins.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end space-x-2">
                    {canChange(ins.customerId) && (
                    <button
                      onClick={() => insurance.handleEdit(ins)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    )}
                    {canDelete(ins.customerId) && (
                    <button
                      onClick={() => insurance.handleDelete(ins.id)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      <FormModal
        isOpen={insurance.showForm}
        onClose={insurance.handleCancel}
        title={insurance.editing ? 'Edit Insurance' : 'Add New Insurance'}
        subtitle={insurance.editing ? 'Update insurance information' : 'Create a new insurance provider'}
        icon={Shield}
        maxWidth="md"
      >
        <form onSubmit={insurance.handleSubmit} className="space-y-4">
              <FormSelect
                label="Customer"
                value={insurance.form.customerId}
                onChange={(e) => insurance.setForm({ ...insurance.form, customerId: e.target.value })}
                options={customerOptions}
                placeholder="Select customer (optional)..."
                backgroundColor="primary"
              />
              <FormInput
                label="Name"
                required
                type="text"
                value={insurance.form.name}
                onChange={(e) => insurance.setForm({ ...insurance.form, name: e.target.value })}
                backgroundColor="primary"
              />
              <FormTextarea
                label="Description"
                value={insurance.form.description}
                onChange={(e) => insurance.setForm({ ...insurance.form, description: e.target.value })}
                rows={3}
                backgroundColor="primary"
              />
              <FormCheckbox
                label="Active"
                checked={insurance.form.isActive}
                onChange={(e) => insurance.setForm({ ...insurance.form, isActive: e.target.checked })}
                backgroundColor="primary"
              />
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={insurance.handleCancel}
                  className="px-4 py-2 border rounded-lg transition-colors hover:bg-gray-50"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-text-on-accent)'
                  }}
                  disabled={insurance.isCreating || insurance.isUpdating}
                >
                  {insurance.isCreating || insurance.isUpdating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={insurance.showDeleteModal}
        onClose={insurance.handleCancelDelete}
        onConfirm={insurance.handleConfirmDelete}
        title="Delete Insurance"
        message="Are you sure you want to delete this insurance? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={insurance.isDeleting}
      />
    </div>
  );
};

